import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import {
    AgenticTestResultBadge,
    Badge,
    TagIconBadge
} from "../../components/badge";
import { BigTextPreview } from "../../components/big-text-preview";
import { Button, ButtonRow, DangerButton, PrimaryButton } from "../../components/button";
import { Card, CardContent, CardHeader } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DataPickerControl } from "../../components/data-picker";
import { DateTimeLabel } from "../../components/datetime-label";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { NumberInput, TextArea, TextInput } from "../../components/form";
import { GatewayPickerControl } from "../../components/gateway-picker";
import { Icon } from "../../components/icon";
import { JsonControl } from "../../components/json-control";
import { Link } from "../../components/link";
import { Section } from "../../components/layout";
import { TabControl } from "../../components/tabcontrol";
import {
    DefaultRow,
    Table,
    TableActionCell,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderActionCell,
    TableHeaderCell,
    TableRow
} from "../../components/table";
import {
    CheckWrapper,
    CopiableText,
    DashboardTextHeading,
    Heading,
    MutedText,
    Paragraph
} from "../../components/text";
import { displayFormControl } from "../../ui/alert";
import { closeFlyout, showFlyout } from "../../ui/flyout";
import { formatFlexibibleCurrency, formatFloat, formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { showSuccessToast } from "../../ui/toast";
import { cond, valueFrom } from "../../util";

export default function (ctx) {
    const testId = ctx.params.id;
    const isCreating = testId === "create";
    const test = el.state.create(null);
    const runs = el.state.create(null);

    async function refreshRuns() {
        runs.value = (await api.getJson(`/api/v1/agentic-tests/${testId}/runs`)).data;
    }

    if (!isCreating) {
        runOperation("Loading Agentic Test...", async () => {
            test.value = (await api.getJson(`/api/v1/agentic-tests/${testId}`)).data;
            await refreshRuns();
        });
    }

    return el.state.listening([test], () =>
        Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                    onClick() {
                        history.go(-1);
                        page();
                    }
                })
            ),
            DashboardTitle(
                isCreating
                    ? "New Agentic Test"
                    : DashboardTextHeading(
                        el.fragment(
                            test.value?.name || MutedText("Unnamed test"),
                            MutedText(" ~#", testId.slice(-6))
                        ),
                        "Agentic Test details"
                    ),
                "Configure a repeatable gateway simulation and inspect its retained runs."
            ),
            Section(
                CommandBar(
                    Button(
                        Icon("save-line"),
                        isCreating ? "Create Agentic Test" : "Save changes",
                        {
                            onClick() {
                                runOperation(
                                    isCreating
                                        ? "Creating Agentic Test..."
                                        : "Saving Agentic Test...",
                                    async () => {
                                        const data = {
                                            name: valueFrom("agenticTestName"),
                                            gateway: valueFrom("agenticTestGateway"),
                                            goal: valueFrom("agenticTestGoal"),
                                            validation_criteria: valueFrom("agenticTestValidationCriteria")?.nullWhenEmpty(),
                                            start: valueFrom("agenticTestStart"),
                                            max_turns: valueFrom("agenticTestMaxTurns"),
                                            minimum_turns: valueFrom("agenticTestMinimumTurns"),
                                            allow_user_exit: valueFrom("agenticTestAllowUserExit"),
                                            judge_start_turn: valueFrom("agenticTestJudgeStartTurn"),
                                            loss_threshold: valueFrom("agenticTestLossThreshold"),
                                            base_threshold: valueFrom("agenticTestBaseThreshold"),
                                            profile: valueFrom("agenticTestProfile"),
                                            user_sampling: {
                                                top_k: valueFrom("agenticTestUserSamplingTopK"),
                                                max_decay: valueFrom("agenticTestUserSamplingMaxDecay")
                                            },
                                            metadata: valueFrom("agenticTestMetadata"),
                                            resources: valueFrom("agenticTestResources"),
                                            hooks: valueFrom("agenticTestHooks"),
                                            external_user_id: valueFrom("agenticTestExternalUserId")?.nullWhenEmpty(),
                                            cron: valueFrom("agenticTestCron")?.nullWhenEmpty(),
                                            enabled: valueFrom("agenticTestEnabled"),
                                            notification_threshold: valueFrom("agenticTestNotificationThreshold"),
                                            recovery_notification: valueFrom("agenticTestRecoveryNotification")
                                        };
                                        const response = isCreating
                                            ? await api.postJson(
                                                "/api/v1/agentic-tests",
                                                data
                                            )
                                            : await api.patchJson(
                                                `/api/v1/agentic-tests/${testId}`,
                                                data
                                            );

                                        showSuccessToast(
                                            isCreating
                                                ? "Agentic Test created."
                                                : "Agentic Test saved."
                                        );
                                        if (isCreating) {
                                            page(`/dashboard/tests/${response.data.id}`);
                                        } else {
                                            test.value = response.data;
                                        }
                                    }
                                );
                            }
                        }
                    ),
                    !isCreating && Button(Icon("play-line"), "Run test", {
                        onClick() {
                            showFlyout(
                                Paragraph("Run this Agentic Test now? This execution uses your account balance."),
                                PrimaryButton(Icon("play-fill"), "Run test", {
                                    onClick(event) {
                                        runOperation("Starting Agentic Test...", async () => {
                                            const response = await api.postJson(
                                                `/api/v1/agentic-tests/${testId}/runs`,
                                                {}
                                            );
                                            closeFlyout(event.target);
                                            page(
                                                `/dashboard/tests/${testId}/runs/${response.data.id}`
                                            );
                                        });
                                    }
                                })
                            );
                        }
                    })
                )
            ),
            Section(
                TabControl([
                    {
                        title: "Test",
                        icon: Icon("file-settings-line"),
                        contents: el.fragment(
                            Heading(
                                "Test setup",
                                "Define the gateway, simulated user goal and identity used by this test."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "agenticTestName",
                                        value: test.value?.name || "",
                                        renderValue(value) {
                                            return Button(value || MutedText("Unnamed test"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Test name", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => !!x?.trim(), "Enter a test name."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Name"),
                                        Paragraph("A recognizable name for this Agentic Test.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestGateway",
                                        value: test.value?.gateway || null,
                                        renderValue(value) {
                                            return Button(value || MutedText("No gateway selected"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: GatewayPickerControl({ value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => !!x, "Select a gateway."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("AI gateway"),
                                        Paragraph("Gateway evaluated by this test.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestGoal",
                                        value: test.value?.goal || "",
                                        renderValue(value) {
                                            return BigTextPreview(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: TextArea(
                                                    null,
                                                    value,
                                                    {
                                                        placeholder: "Describe the simulated user's goal..."
                                                    }
                                                ),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => !!x?.trim(), "Enter a goal."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Goal"),
                                        Paragraph("Outcome shared with the simulated user and judge.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestExternalUserId",
                                        value: test.value?.external_user_id || "",
                                        renderValue(value) {
                                            return Button(value || MutedText("Not set"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(
                                                    null,
                                                    "Optional external user ID",
                                                    { value }
                                                ),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("External user ID"),
                                        Paragraph(
                                            "Optional identity forwarded to the gateway for usage attribution "
                                            + "and conversation tracking."
                                        )
                                    )
                                )
                            ),
                            Heading(
                                "Conversation context",
                                "Provide initial messages and metadata that should be available when each run starts."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "agenticTestStart",
                                        value: test.value?.start || [],
                                        renderValue(value) {
                                            return value?.length
                                                ? BigTextPreview(JSON.stringify(value, null, 2), "json")
                                                : MutedText("No start messages");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => Array.isArray(x), "Start messages must be a JSON array."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Start messages"),
                                        Paragraph("Conversation messages inserted before the simulated user begins the test.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestResources",
                                        value: test.value?.resources || [],
                                        renderValue(value) {
                                            return value?.length
                                                ? BigTextPreview(JSON.stringify(value, null, 2), "json")
                                                : MutedText("No external resources");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => Array.isArray(x) && x.length <= 16 && x.every(resource =>
                                                        resource && !Array.isArray(resource) &&
                                                        ["Text", "RemoteResource"].includes(resource.type) &&
                                                        (resource.hook === undefined || ["OnStart", "OnTurn"].includes(resource.hook)) &&
                                                        (resource.visibility === undefined || ["Internal", "Gateway", "Both"].includes(resource.visibility)) &&
                                                        (resource.cacheDuration === undefined || typeof resource.cacheDuration === "number" && resource.cacheDuration >= 0 && resource.cacheDuration <= 21600) &&
                                                        typeof resource.data === "string" && !!resource.data.trim()),
                                                    "Resources must contain at most 16 valid objects; cacheDuration must be between 0 and 21600 seconds."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("External resources"),
                                        Paragraph("Additional text or URL content resolved OnStart or OnTurn and exposed to Internal generators, the Gateway, or Both. cacheDuration is expressed in seconds from 0 to 21600.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestMetadata",
                                        value: test.value?.metadata || {},
                                        renderValue(value) {
                                            return Object.keys(value || {}).length
                                                ? BigTextPreview(JSON.stringify(value, null, 2), "json")
                                                : MutedText("No metadata");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x && !Array.isArray(x), "Metadata must be a JSON object."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Metadata"),
                                        Paragraph("Additional key-value data forwarded to the gateway for every run.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Evaluation",
                        icon: Icon("instance-line"),
                        contents: el.fragment(
                            Heading(
                                "Simulation profile",
                                "Configure how the simulated user behaves and how long the conversation can continue."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "agenticTestProfile",
                                        value: test.value?.profile || "medium",
                                        renderValue(value) {
                                            return cond(
                                                [value?.toLowerCase() == "low", Badge(
                                                    "Low",
                                                    "var(--fr-300)"
                                                )],
                                                [value?.toLowerCase() == "medium", Badge(
                                                    "Medium",
                                                    "var(--info)"
                                                )],
                                                [value?.toLowerCase() == "high", Badge(
                                                    "High",
                                                    "var(--success)"
                                                )],
                                                [true, Badge(value)]
                                            );
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    value,
                                                    data: [
                                                        { value: "low", text: "Low" },
                                                        { value: "medium", text: "Medium" },
                                                        { value: "high", text: "High" }
                                                    ]
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Profile"),
                                        Paragraph("Controls the capability and cost of the simulated user and judge models.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestMaxTurns",
                                        value: test.value?.max_turns ?? 10,
                                        renderValue(value) {
                                            return Button(formatNumber(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 2, max: 64, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum turns"),
                                        Paragraph("Maximum number of simulated user turns before the run ends, from 2 to 64.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestMinimumTurns",
                                        value: test.value?.minimum_turns ?? 1,
                                        renderValue(value) {
                                            return Button(formatNumber(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 63, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Minimum turns"),
                                        Paragraph("First turn when the simulated user may end the conversation. Must be less than maximum turns.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestAllowUserExit",
                                        value: test.value?.allow_user_exit ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "allowed", "not allowed");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Allow user exit"),
                                        Paragraph("Show the simulated user the conversation exit option after minimum turns.")
                                    )
                                )
                            ),
                            Heading(
                                "User sampling",
                                "Configure the sampling behavior of the model that simulates the user."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "agenticTestUserSamplingTopK",
                                        value: test.value?.user_sampling?.top_k ?? 0.4,
                                        renderValue(value) {
                                            return Button(formatFloat(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({
                                                    value,
                                                    min: 0,
                                                    max: 2,
                                                    step: 0.01
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Top K"),
                                        Paragraph("Controls the diversity of candidate responses from the simulated user.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestUserSamplingMaxDecay",
                                        value: test.value?.user_sampling?.max_decay ?? 0.02,
                                        renderValue(value) {
                                            return Button(formatFloat(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({
                                                    value,
                                                    min: 0,
                                                    max: 1,
                                                    step: 0.01
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum decay"),
                                        Paragraph("Limits how quickly response diversity decreases across turns.")
                                    )
                                )
                            ),
                            Heading(
                                "Judge thresholds",
                                "Define judge-only criteria, when evaluation begins and which scores indicate success or loss."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "agenticTestValidationCriteria",
                                        value: test.value?.validation_criteria || "",
                                        renderValue(value) {
                                            return BigTextPreview(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: TextArea(
                                                    null,
                                                    value,
                                                    {
                                                        placeholder: "Describe the conditions the judge must verify..."
                                                    }
                                                ),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Validation criteria"),
                                        Paragraph("Optional requirements used only by the judge. They do not guide the simulated user.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestHooks",
                                        value: test.value?.hooks || [],
                                        renderValue(value) {
                                            return value?.length
                                                ? BigTextPreview(JSON.stringify(value, null, 2), "json")
                                                : MutedText("No validation hooks");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => Array.isArray(x), "Hooks must be a JSON array."],
                                                    [x => Array.isArray(x) && x.length <= 16, "You can configure up to 16 hooks."],
                                                    [x => Array.isArray(x) && x.every(hook => hook && typeof hook.event === "string" && typeof hook.url === "string"), "Each hook must contain event and url strings."],
                                                    [x => Array.isArray(x) && x.every(hook => hook && ["before-test", "after-test", "before-inference", "after-inference", "context-changed"].includes(hook.event)), "Each hook event must be supported."],
                                                    [x => Array.isArray(x) && x.every(hook => hook && typeof hook.url === "string" && (hook.url.startsWith("http://") || hook.url.startsWith("https://"))), "Hook URLs must use HTTP or HTTPS."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Validation hooks"),
                                        Paragraph("HTTP callbacks invoked during persisted runs. A 2xx response continues the run; a failed request or non-2xx response interrupts it. before-inference and after-inference require an AI Gateway.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestJudgeStartTurn",
                                        value: test.value?.judge_start_turn ?? 1,
                                        renderValue(value) {
                                            return Button(formatNumber(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 63, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Judge start turn"),
                                        Paragraph("First turn whose progress is evaluated by the judge. Must be less than maximum turns.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestLossThreshold",
                                        value: test.value?.loss_threshold ?? 0.2,
                                        renderValue(value) {
                                            return Button(formatFloat(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0.01, max: 0.99, step: 0.01 }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Loss threshold"),
                                        Paragraph("Scores below this threshold count toward a failed conversation.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestBaseThreshold",
                                        value: test.value?.base_threshold ?? 0.9,
                                        renderValue(value) {
                                            return Button(formatFloat(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0.01, max: 0.99, step: 0.01 }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Success threshold"),
                                        Paragraph("Score at which the test goal is considered successfully achieved.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Schedule",
                        icon: Icon("calendar-line"),
                        contents: el.fragment(
                            Heading(
                                "Scheduled execution",
                                "Run this test periodically using a standard cron expression with a minimum interval of five minutes."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "agenticTestCron",
                                        value: test.value?.cron || "",
                                        renderValue(value) {
                                            return Button(value || MutedText("Manual only"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "*/15 * * * *", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Cron expression"),
                                        Paragraph("Leave empty to run this test manually only.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestEnabled",
                                        value: test.value?.enabled ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Scheduled runs"),
                                        Paragraph("Enable or pause new runs created from the cron expression.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Notifications",
                        icon: Icon("notification-4-line"),
                        contents: el.fragment(
                            Heading(
                                "Failure notifications",
                                "Control when repeated failures trigger an account notification and whether recovery is reported."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "agenticTestNotificationThreshold",
                                        value: test.value?.notification_threshold ?? 1,
                                        renderValue(value) {
                                            return Button(formatNumber(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 100, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Failure threshold"),
                                        Paragraph("Number of consecutive failed runs required before sending a notification.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "agenticTestRecoveryNotification",
                                        value: test.value?.recovery_notification ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Recovery notification"),
                                        Paragraph("Notify the account when a run succeeds after consecutive failures.")
                                    )
                                )
                            )
                        )
                    },
                    !isCreating && {
                        title: "Runs",
                        icon: Icon("history-line"),
                        contents: el.fragment(
                            Heading(
                                "Test runs",
                                "Inspect retained results, usage and conversation history for this Agentic Test."
                            ),
                            Section(
                                CommandBar(
                                    Button(Icon("refresh-line"), "Refresh", {
                                        onClick() {
                                            runOperation("Loading test runs...", refreshRuns);
                                        }
                                    })
                                )
                            ),
                            Section(
                                Card(
                                    CardHeader("Runs"),
                                    CardContent(
                                        Table(
                                            { classList: ["hoverable"] },
                                            TableHeader(
                                                TableHeaderCell("Run"),
                                                TableHeaderCell("Result"),
                                                TableHeaderCell("Created"),
                                                TableHeaderCell("Finished"),
                                                TableHeaderCell("Score"),
                                                TableHeaderCell("Cost"),
                                                TableHeaderActionCell("Actions")
                                            ),
                                            el.state.listening([runs], () =>
                                                TableBody(
                                                    runs.value?.map(run =>
                                                        TableRow(
                                                            TableCell(
                                                                CopiableText(
                                                                    `#${run.id.slice(-4)}`,
                                                                    run.id
                                                                )
                                                            ),
                                                            TableCell(
                                                                AgenticTestResultBadge(run)
                                                            ),
                                                            TableCell(
                                                                DateTimeLabel(run.created_at)
                                                            ),
                                                            TableCell(
                                                                run.finished_at
                                                                    ? DateTimeLabel(run.finished_at)
                                                                    : MutedText("-")
                                                            ),
                                                            TableCell(
                                                                run.score != null
                                                                    ? run.score >= 0
                                                                        ? TagIconBadge(
                                                                            Icon(run.score >= 0.96 ? "check-line" : "alert-line"),
                                                                            formatFloat(run.score),
                                                                            run.score >= 0.96 ? "var(--success)" : "var(--warn)"
                                                                        )
                                                                        : formatFloat(run.score)
                                                                    : MutedText("-")
                                                            ),
                                                            TableCell(
                                                                run.cost != null
                                                                    ? formatFlexibibleCurrency(run.cost)
                                                                    : MutedText("-")
                                                            ),
                                                            TableActionCell(
                                                                ButtonRow(
                                                                    Button(
                                                                        Icon("arrow-right-double-line"),
                                                                        "Inspect",
                                                                        {
                                                                            onClick() {
                                                                                page(
                                                                                    `/dashboard/tests/${testId}/runs/${run.id}`
                                                                                );
                                                                            }
                                                                        }
                                                                    ),
                                                                    ["pending", "running"].includes(
                                                                        run.state?.toLowerCase()
                                                                    ) && DangerButton(
                                                                        Icon("stop-circle-line"),
                                                                        "Cancel",
                                                                        {
                                                                            onClick() {
                                                                                showFlyout(
                                                                                    Paragraph(
                                                                                        "Cancel this run? Partial execution "
                                                                                        + "data will be retained."
                                                                                    ),
                                                                                    DangerButton(
                                                                                        "Cancel run",
                                                                                        {
                                                                                            onClick(event) {
                                                                                                runOperation(
                                                                                                    "Cancelling run...",
                                                                                                    async () => {
                                                                                                        await api.postJson(
                                                                                                            "/api/v1/agentic-tests/"
                                                                                                            + testId
                                                                                                            + `/runs/${run.id}/cancel`,
                                                                                                            {}
                                                                                                        );
                                                                                                        closeFlyout(event.target);
                                                                                                        await refreshRuns();
                                                                                                    }
                                                                                                );
                                                                                            }
                                                                                        }
                                                                                    )
                                                                                );
                                                                            }
                                                                        }
                                                                    )
                                                                )
                                                            )
                                                        )
                                                    )?.nullWhenEmpty() || DefaultRow("This test has not been run yet.")
                                                )
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    }
                ].filter(Boolean), {
                    type: "fixed"
                })
            )
        )
    );
}
